// Fensterlicht: Fensterkreuz, Blattschatten und der echte Schatten der freigestellten Pflanze.
// Wird erst nach dem ersten Seitenaufbau geladen und blockiert nie den LCP.
// Three.js liegt als schlanke, selbst gehostete Datei im Theme (assets/three-light.min.js).
(() => {

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  precision mediump float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uAspect;
  uniform vec2 uSun;        // Zeiger, -1..1
  uniform float uDay;       // Scrollfortschritt, 0..1
  uniform float uStrength;
  uniform sampler2D uPlant; // freigestellte Pflanze (Alpha = Silhouette)
  uniform vec4 uPlantRect;  // x, y (unten), Breite, Höhe in Canvas-UV
  uniform float uPlantMix;  // Überblendung beim Pflanzenwechsel

  float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
  }
  float fbm(vec2 p) {
    float v = 0.0, a = 0.5;
    for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.03; a *= 0.5; }
    return v;
  }

  float plantAlpha(vec2 uv) {
    vec2 p = (uv - uPlantRect.xy) / uPlantRect.zw;
    float inside = step(0.0, p.x) * step(p.x, 1.0) * step(0.0, p.y) * step(p.y, 1.0);
    return texture2D(uPlant, clamp(p, 0.0, 1.0)).a * inside;
  }

  void main() {
    vec2 p = vec2(vUv.x * uAspect, vUv.y);

    // Sonnenstand: Scherung und Versatz des Lichtflecks
    float shear = mix(-0.62, -0.18, uDay) + uSun.x * 0.07;
    vec2 q = p;
    q.x += (q.y - 0.5) * shear;
    q += vec2(mix(-0.10, 0.28, uDay) * uAspect + uSun.x * 0.05, uSun.y * 0.035);

    // Fensterfläche mit weicher Kante
    vec2 center = vec2(uAspect * 0.58, 0.56);
    vec2 size = vec2(uAspect * 0.62, 0.92);
    vec2 w = (q - center) / size;
    float soft = 0.035 + 0.05 * (1.0 - vUv.y);
    float inside = smoothstep(0.5, 0.5 - soft, abs(w.x)) * smoothstep(0.5, 0.5 - soft, abs(w.y));

    // Fensterkreuz
    float bar = 0.018;
    float mull = 1.0 - smoothstep(bar, bar + soft * 0.6, abs(w.x));
    mull = max(mull, 1.0 - smoothstep(bar, bar + soft * 0.6, abs(w.y + 0.08)));
    float light = inside * (1.0 - mull);

    // Blattschatten von draußen, leicht im Luftzug
    vec2 sway = vec2(sin(uTime * 0.45) * 0.035, cos(uTime * 0.37) * 0.025);
    float leaves = fbm(q * vec2(2.4, 2.0) + sway + vec2(3.1, 7.4));
    float leafMask = smoothstep(0.56, 0.6, leaves) * smoothstep(0.15, 0.55, vUv.y) * (1.0 - smoothstep(0.35, 0.6, vUv.x));
    light *= 1.0 - leafMask * 0.75;

    // Schatten der Pflanze: ein weiches, leicht versetztes Abbild der Silhouette auf der Wand.
    // Je weiter ein Teil vom Sockel entfernt ist, desto weicher wird sein Schatten.
    float base = uPlantRect.y;
    float height = clamp((vUv.y - base) / uPlantRect.w, 0.0, 1.0);
    vec2 offset = vec2(0.028 - uSun.x * 0.018 + uDay * 0.02, -0.018 - uSun.y * 0.006);
    vec2 src = vUv - offset;
    float blur = 0.003 + height * 0.012;
    float plant = plantAlpha(src) * 0.24;
    plant += plantAlpha(src + vec2(blur, 0.0)) * 0.12;
    plant += plantAlpha(src - vec2(blur, 0.0)) * 0.12;
    plant += plantAlpha(src + vec2(0.0, blur)) * 0.12;
    plant += plantAlpha(src - vec2(0.0, blur)) * 0.12;
    plant += plantAlpha(src + vec2(blur, blur) * 0.7) * 0.07;
    plant += plantAlpha(src - vec2(blur, blur) * 0.7) * 0.07;
    plant += plantAlpha(src + vec2(blur, -blur) * 0.7) * 0.07;
    plant += plantAlpha(src + vec2(-blur, blur) * 0.7) * 0.07;
    plant *= mix(0.55, 0.75, inside) * uPlantMix;

    float shadow = max((1.0 - light) * uStrength, plant);
    vec3 tint = vec3(0.78, 0.68, 0.55);
    gl_FragColor = vec4(mix(vec3(1.0), tint, shadow), 1.0);
  }
`;

function initLight(THREE, canvas, { reducedMotion = false, animate = true } = {}) {
  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, alpha: false, powerPreference: 'low-power' });
  } catch (e) {
    return null; // Kein WebGL: Seite funktioniert ohne Lichteffekt
  }
  renderer.setPixelRatio(0.75);

  const uniforms = {
    uTime: { value: 0 },
    uAspect: { value: 1 },
    uSun: { value: new THREE.Vector2(0, 0) },
    uDay: { value: 0.35 },
    uStrength: { value: 0.3 },
    uPlant: { value: null },
    uPlantRect: { value: new THREE.Vector4(2, 2, 0.1, 0.1) },
    uPlantMix: { value: 0 },
  };
  const scene = new THREE.Scene();
  const camera = new THREE.Camera();
  const geometry = new THREE.PlaneGeometry(2, 2);
  const material = new THREE.ShaderMaterial({ uniforms, vertexShader, fragmentShader, depthTest: false });
  scene.add(new THREE.Mesh(geometry, material));
  const loader = new THREE.TextureLoader();

  // 30 Bilder pro Sekunde reichen für sanft wehende Blattschatten und schonen Akku und Prozessor
  const frameInterval = 1000 / 30;
  let plantEl = null;

  function measurePlant() {
    if (!plantEl) return;
    const c = canvas.getBoundingClientRect();
    const r = plantEl.getBoundingClientRect();
    if (!c.width || !r.width) return;
    uniforms.uPlantRect.value.set(
      (r.left - c.left) / c.width,
      1 - (r.bottom - c.top) / c.height,
      r.width / c.width,
      r.height / c.height,
    );
  }
  function resize() {
    const { clientWidth: w, clientHeight: h } = canvas;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    uniforms.uAspect.value = w / h;
    measurePlant();
  }
  const ro = new ResizeObserver(() => { resize(); draw(); });
  ro.observe(canvas);
  resize();

  let running = false, visible = true, rafId = 0, last = 0;
  const clock = new THREE.Clock();
  const draw = () => renderer.render(scene, camera);

  function frame(now) {
    rafId = requestAnimationFrame(frame);
    if (frameInterval && now - last < frameInterval) return;
    last = now;
    uniforms.uTime.value = clock.getElapsedTime();
    draw();
  }
  function start() {
    if (running || reducedMotion || !animate || !visible || document.hidden) return;
    running = true;
    clock.start();
    rafId = requestAnimationFrame(frame);
  }
  function stop() {
    running = false;
    cancelAnimationFrame(rafId);
  }

  const io = new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    visible ? start() : stop();
  });
  io.observe(canvas);
  const onVisibility = () => (document.hidden ? stop() : start());
  document.addEventListener('visibilitychange', onVisibility);

  draw();
  start();

  return {
    uniforms,
    renderOnce() { if (!running) draw(); },
    // Pflanze tauschen: Textur laden, Position messen, dann einblenden
    setPlant(el, src) {
      plantEl = el;
      return new Promise(resolve => {
        loader.load(src, tex => {
          tex.colorSpace = THREE.NoColorSpace;
          tex.generateMipmaps = false;
          tex.minFilter = THREE.LinearFilter;
          uniforms.uPlant.value?.dispose();
          uniforms.uPlant.value = tex;
          measurePlant();
          draw();
          resolve();
        }, undefined, () => resolve());
      });
    },
    measurePlant() { measurePlant(); if (!running) draw(); },
    destroy() {
      stop();
      io.disconnect();
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      uniforms.uPlant.value?.dispose();
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    },
  };
}

  window.MZLight = {
    init: (canvas, opts) => import(window.MZ.assets.three).then(THREE => initLight(THREE, canvas, opts)),
  };
})();
